/**
 * Metric names for the USLegal footer links — MEP-1377.
 *
 * On the legacy render every footer link carries its own `data-send-metric`, printed
 * by the Blade footer partial. ME builds its footer from Herald payload slots, which
 * carry a label and an href and nothing else, so the names have to be resolved on the
 * client instead.
 *
 * The name is a property of the *column*, not of the link: `/bankruptcy` is
 * `usl_footer_category_packages_clicks` under "Form Packages" and
 * `usl_footer_categories_clicks` under "Form Categories". A few links override their
 * column. Hence the two tables below — a per-column default plus per-href exceptions —
 * read off the live footer of
 * `https://www.uslegalforms.com/form-library/61161-nj-lgccc-2r-a`, whose seven columns
 * match ME's footer groups one for one, in the same order and with the same link
 * counts.
 *
 * Note the older form pages still render a different footer (no mobile accordion,
 * fewer columns). Keying on the column title rather than on position degrades
 * gracefully there: a recognised title resolves, an unrecognised one emits nothing
 * rather than an invented event.
 */

/** Column title (lower-cased) → the metric every link in that column emits. */
const FOOTER_COLUMN_METRICS: Record<string, string> = {
  'form packages': 'usl_footer_category_packages_clicks',
  'form categories': 'usl_footer_categories_clicks',
  'customer service': 'usl_footer_other_clicks',
  'legal guides': 'usl_footer_other_clicks',
  'our customers': 'usl_footer_other_clicks',
  'our sites': 'usl_footer_other_clicks',
  // "Prepared for you" is deliberately absent: both of its links carry their own name
  // below and the legacy footer shows no column-wide default, so an unrecognised link
  // there should stay silent instead of borrowing someone else's event.
};

/** Normalised href path → the metric that overrides the column default. */
const FOOTER_LINK_METRICS: Record<string, string> = {
  '/formspackages': 'usl_footer_packages_clicks',
  '/all-forms': 'usl_footer_catalog_clicks',
  '/form-library': 'usl_footer_pf_catalog_clicks',
  '/es/forms': 'usl_es_forms_click_in_footer',
  '/localized-forms': 'usl_localized_forms_click_in_footer',
  '/spec-forms': 'usl_footer_spec_clicks',
  '/forms-kit': 'usl_footer_forms_kit_clicks',
  '/notarize-documents-online': 'usl_notarize_click_in_footer',
  '/incorporation-services': 'usl_inc_services_click_in_footer',
};

/**
 * Everything below is scoped to the USLegal footer root, and that scoping is load
 * bearing rather than tidiness: `footer-menu-type-1` is a shared component used by the
 * generic `footers/type-1…5` and by `signnow/footer/type-1`. Without this gate, a
 * SignNow footer column titled "Customer Service" or "Our Sites" would silently start
 * emitting USLegal metric names to signnow.com. No current footer uses a colliding
 * title, but the collision has to be impossible, not merely absent.
 */
const USLEGAL_FOOTER_SELECTOR = 'ul-footer-layout-type-1, .ul-footer-layout-type-1';

/**
 * The footer renders its navigation twice, exactly as the legacy footer does: desktop
 * columns as `footer-menu-type-1`, and a mobile accordion whose panels sit outside
 * those components. Both have to resolve — form pages skew mobile, and a desktop-only
 * resolver would leave the arm reading zero on the traffic that matters most.
 */
const FOOTER_GROUP_SELECTOR = 'footer-menu-type-1, .ul-footer-layout-type-1__accordion-item';
const GROUP_TITLE_SELECTOR = '[slot="title"], .ul-footer-layout-type-1__accordion-title';

/**
 * The legal bar (Terms of Service, Privacy Notice, Content Takedown Policy, Bug Bounty
 * Program) is not a titled column, but the legacy footer does track it — as
 * `usl_footer_other_clicks`, same as the untitled links around it.
 */
const LEGAL_LINKS_SELECTOR = '.ul-footer-layout-type-1__legal-links';
const LEGAL_LINKS_METRIC = 'usl_footer_other_clicks';

/**
 * Reduces an href to a comparable path: no host, no query, no trailing slash.
 * ME renders `/bankruptcy` where the legacy footer renders
 * `https://www.uslegalforms.com/bankruptcy/`.
 */
const normalizeHref = (href: string | null): string | null => {
  if (!href) return null;

  try {
    const { pathname } = new URL(href, window.location.origin);
    const trimmed = pathname.replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed.toLowerCase();
  } catch {
    return null;
  }
};

const getGroupTitle = (group: Element): string | null => {
  const title = group.querySelector(GROUP_TITLE_SELECTOR);
  return title?.textContent?.trim().toLowerCase() || null;
};

/**
 * Resolves the metric name for a clicked footer link. Returns `null` when the link is
 * outside the USLegal footer entirely, and when it is inside but not one the legacy
 * footer tracks — the social icons, the app-store badges, the brand logo and the phone
 * number carry no metric on the legacy render either.
 */
export const resolveFooterMetric = (element: Element): string | null => {
  if (!element.closest(USLEGAL_FOOTER_SELECTOR)) return null;

  const group = element.closest(FOOTER_GROUP_SELECTOR);
  if (!group) {
    return element.closest(LEGAL_LINKS_SELECTOR) ? LEGAL_LINKS_METRIC : null;
  }

  const override = FOOTER_LINK_METRICS[normalizeHref(element.getAttribute('href')) ?? ''];
  if (override) return override;

  const title = getGroupTitle(group);
  return (title && FOOTER_COLUMN_METRICS[title]) || null;
};

/**
 * The footer links that also feed the clickstream channel. Legacy marks every one of
 * them `data-clickstream-event="footer_click"`, so anything the metric resolver
 * recognises as a tracked footer link belongs here too.
 */
export const FOOTER_CLICKSTREAM_EVENT = 'footer_click';
